import { FC } from 'react';
import { useMediaQuery } from 'react-responsive';

import { Drawer, DrawerProps } from 'antd';
import {
  PLAN_TYPE_EPO,
  PLAN_TYPE_HMO,
  PLAN_TYPE_OAP,
  PLAN_TYPE_POS,
  PLAN_TYPE_PPO,
} from 'modules/benefitsWellness/constants/BenefitConstants';
import { MediaWidth } from 'constants/mediaConstants';
import iconBack from 'images/icons/icon-back.png';
import {
  WHAT_IS_PPO,
  WHAT_IS_POS,
  WHAT_IS_OAP,
  WHAT_IS_EPO,
  WHAT_IS_HMO,
  WHAT_IS_HSA_ELIGIBLE_PLAN,
  PPO_DESCRIPTION_1,
  PPO_DESCRIPTION_2,
  POS_DESCRIPTION,
  OAP_DESCRIPTION,
  EPO_DESCRIPTION,
  HMO_DESCRIPTION_1,
  HMO_DESCRIPTION_2,
  HSA_DESCRIPTION_1,
  HSA_DESCRIPTION_2,
  LEARN_MORE_ABOUT_HSAS,
  LEARN_MORE_ABOUT_PLAN_TYPE,
  HOW_OTHER_PLANS_COMPARE,
  HEALTH_MAINTENANCE_ORGANIZATION,
  PREFERRED_PROVIDER_ORGANIZATION,
  EXCLUSIVE_PROVIDER_ORGANIZATION,
  POINT_OF_SERVICE,
  SELF_REFERRAL,
  PRIMARY_DOC_REFERRAL,
  NO_COINSURANCE,
  NO_DEDUCTIBLE,
  OUT_OF_NETWORK_COVERAGE,
  PLAN_COMPARISON_DESCRIPTION,
  OAP_FOOTNOTE_DESCRIPTION,
} from 'constants/languageConstants';
import { getText } from 'utils/stringUtil';
import { useAppSelector } from 'hooks/redux';
import styles from './planTypeInfoDrawer.module.less';

interface PlanTypeInfoDrawerProps extends DrawerProps {
  planType: string;
  isDrawerOpen: boolean;
  isHSA: boolean;
}

const PPO = ({ language }: { language: 'ENGLISH' | 'SPANISH' | undefined }) => {
  return (
    <div className={styles.planTypeSection}>
      {/* <ReactPlayer
        width="100%"
        height={338}
        controls={true}
        url="https://www.youtube.com/watch?v=S9qGbbSroSE"
      /> */}
      <div className={styles.sectionInfo}>{getText(WHAT_IS_PPO, language)}</div>
      <p>{getText(PPO_DESCRIPTION_1, language)}</p>
      <div className={styles.paraSpacing}>
        {getText(PPO_DESCRIPTION_2, language)}
      </div>
    </div>
  );
};

const POS = ({ language }: { language: 'ENGLISH' | 'SPANISH' | undefined }) => {
  return (
    <div className={styles.planTypeSection}>
      <div className={styles.sectionInfo}>{getText(WHAT_IS_POS, language)}</div>
      <p>{getText(POS_DESCRIPTION, language)}</p>
    </div>
  );
};

const OAP = ({ language }: { language: 'ENGLISH' | 'SPANISH' | undefined }) => {
  return (
    <div className={styles.planTypeSection}>
      <div className={styles.sectionInfo}>{getText(WHAT_IS_OAP, language)}</div>
      <p>{getText(OAP_DESCRIPTION, language)}</p>
    </div>
  );
};

const EPO = ({ language }: { language: 'ENGLISH' | 'SPANISH' | undefined }) => {
  return (
    <div className={styles.planTypeSection}>
      {/* <ReactPlayer
        width="100%"
        height={338}
        controls={true}
        url="https://www.youtube.com/watch?v=S9qGbbSroSE"
      /> */}
      <div className={styles.sectionInfo}>{getText(WHAT_IS_EPO, language)}</div>
      <p>{getText(EPO_DESCRIPTION, language)}</p>
    </div>
  );
};

const HMO = ({ language }: { language: 'ENGLISH' | 'SPANISH' | undefined }) => {
  return (
    <div className={styles.planTypeSection}>
      {/* <ReactPlayer
        width="100%"
        height={338}
        controls={true}
        url="https://www.youtube.com/watch?v=S9qGbbSroSE"
      /> */}
      <div className={styles.sectionInfo}>{getText(WHAT_IS_HMO, language)}</div>
      <p>{getText(HMO_DESCRIPTION_1, language)}</p>
      <div className={styles.paraSpacing}>
        {getText(HMO_DESCRIPTION_2, language)}
      </div>
    </div>
  );
};

const HSA = ({ language }: { language: 'ENGLISH' | 'SPANISH' | undefined }) => {
  return (
    <div className={styles.planTypeSection}>
      {/* <ReactPlayer
        width="100%"
        height={338}
        controls={true}
        url="https://www.youtube.com/watch?v=yJGbsHz8eLo"
      /> */}

      <div className={styles.sectionInfo}>
        {getText(WHAT_IS_HSA_ELIGIBLE_PLAN, language)}
      </div>
      <p>{getText(HSA_DESCRIPTION_1, language)}</p>
      <div className={styles.paraSpacing}>
        {getText(HSA_DESCRIPTION_2, language)}
      </div>
    </div>
  );
};

const PlanTypeInfoDrawer: FC<PlanTypeInfoDrawerProps> = ({
  planType,
  isDrawerOpen,
  onClose,
  isHSA,
}: PlanTypeInfoDrawerProps) => {
  const isTablet = useMediaQuery({ maxWidth: MediaWidth.lg });
  const screenWidth = window.innerWidth;
  const language: 'ENGLISH' | 'SPANISH' | undefined = useAppSelector(
    (state) => state.benguide.data?.language
  );
  return (
    <Drawer
      visible={isDrawerOpen}
      onClose={onClose}
      width={!isTablet ? 1000 : '100%'}
      destroyOnClose
      className={styles.drawer}
    >
      <div className={styles.planTypeInfoWrapper}>
        <div className={styles.typeHeader}>
          {isHSA ? (
            <div className={styles.sectionHeader}>
              {screenWidth < 1025 && (
                <>
                  <img
                    className={styles.backArrow}
                    onClick={onClose}
                    src={iconBack}
                  />
                </>
              )}
              {getText(LEARN_MORE_ABOUT_HSAS, language)}
            </div>
          ) : (
            <div className={styles.sectionHeader}>
              {screenWidth < 1025 && (
                <>
                  <img
                    className={styles.backArrow}
                    onClick={onClose}
                    src={iconBack}
                  />
                </>
              )}
              {getText(LEARN_MORE_ABOUT_PLAN_TYPE, language).replace(
                '{planType}',
                planType
              )}
            </div>
          )}

          <div className={styles.splitter}>
            <hr />
          </div>
        </div>
        {planType === PLAN_TYPE_PPO && !isHSA && <PPO language={language} />}
        {isHSA && <HSA language={language} />}
        {planType === PLAN_TYPE_POS && !isHSA && <POS language={language} />}
        {planType === PLAN_TYPE_HMO && !isHSA && <HMO language={language} />}
        {planType === PLAN_TYPE_EPO && !isHSA && <EPO language={language} />}
        {planType === PLAN_TYPE_OAP && !isHSA && <OAP language={language} />}

        <div className={styles.typeSubHeader}>
          <div className={styles.sectionHeader}>
            {getText(HOW_OTHER_PLANS_COMPARE, language)}
          </div>

          <div className={styles.splitter}>
            <hr />
          </div>
        </div>
        <span className={styles.otherPlansDescription}>
          {getText(PLAN_COMPARISON_DESCRIPTION, language)}
        </span>
        <table className={styles.tablePlanInfo}>
          <col></col>
          {planType === PLAN_TYPE_HMO ? (
            <col className={styles.highlightCol}></col>
          ) : (
            <col className={styles.normalCol}></col>
          )}
          {planType === PLAN_TYPE_PPO ? (
            <col className={styles.highlightCol}></col>
          ) : (
            <col className={styles.normalCol}></col>
          )}
          {planType === PLAN_TYPE_EPO || planType === PLAN_TYPE_OAP ? (
            <col className={styles.highlightCol}></col>
          ) : (
            <col className={styles.normalCol}></col>
          )}
          {planType === PLAN_TYPE_POS ? (
            <col className={styles.highlightCol}></col>
          ) : (
            <col className={styles.normalCol}></col>
          )}
          <tr>
            <th className={styles.th1}></th>

            <th className={styles.tmiddle}>
              {planType === PLAN_TYPE_HMO && (
                <div className={styles.highlightHeader} />
              )}
              <span className={styles.heading}>HMO</span>
              <p className={styles.description}>
                {getText(HEALTH_MAINTENANCE_ORGANIZATION, language)}
              </p>
            </th>

            <th className={styles.tmiddle}>
              {planType === PLAN_TYPE_PPO && (
                <div className={styles.highlightHeader} />
              )}
              <span className={styles.heading}>PPO</span>
              <p className={styles.description}>
                {getText(PREFERRED_PROVIDER_ORGANIZATION, language)}
              </p>
            </th>
            <th className={styles.tmiddle}>
              {(planType === PLAN_TYPE_OAP || planType === PLAN_TYPE_EPO) && (
                <div className={styles.highlightHeader} />
              )}
              <span className={styles.heading}>EPO</span>
              <p className={styles.description}>
                {getText(EXCLUSIVE_PROVIDER_ORGANIZATION, language)}
              </p>
            </th>
            <th className={styles.th1}>
              {planType === PLAN_TYPE_POS && (
                <div className={styles.highlightHeader} />
              )}
              <span className={styles.heading}>POS</span>
              <p className={styles.descriptionPOS}>
                {getText(POINT_OF_SERVICE, language)}
              </p>
            </th>
          </tr>
          <tr>
            <td className={styles.titleCol}>
              {getText(SELF_REFERRAL, language)}
            </td>
            <td className={styles.tdBorder}>-</td>
            <td className={styles.tdBorder}>
              <span className={styles.greenDot} />
            </td>
            <td className={styles.tdBorder}>
              <span className={styles.greenDot} />
            </td>
            <td className={styles.tdBorder}>-</td>
          </tr>
          <tr className={styles.tRow}>
            <td className={styles.titleCol}>
              {getText(PRIMARY_DOC_REFERRAL, language)}
            </td>
            {planType === PLAN_TYPE_HMO ? (
              <td className={`${styles.highlightCol} ${styles.light}`}>
                <span className={styles.greenDot} />
              </td>
            ) : (
              <td className={`${styles.darkData} ${styles.bordered}`}>
                <span className={styles.greenDot} />
              </td>
            )}

            {planType === PLAN_TYPE_PPO ? (
              <td className={`${styles.highlightCol} ${styles.light}`}>-</td>
            ) : (
              <td className={`${styles.darkData} ${styles.bordered}`}>-</td>
            )}
            {planType === PLAN_TYPE_EPO || planType === PLAN_TYPE_OAP ? (
              <td className={`${styles.highlightCol} ${styles.light}`}>-</td>
            ) : (
              <td className={`${styles.darkData} ${styles.bordered}`}>-</td>
            )}
            {planType === PLAN_TYPE_POS ? (
              <td
                className={`${styles.highlightCol} ${styles.light} ${styles.borderless}`}
              >
                <span className={styles.greenDot} />
              </td>
            ) : (
              <td className={styles.darkData}>
                <span className={styles.greenDot} />
              </td>
            )}
          </tr>
          <tr>
            <td className={styles.titleCol}>
              {getText(NO_COINSURANCE, language)}
            </td>
            <td className={styles.tdBorder}>
              <span className={styles.greenDot} />
            </td>
            <td className={styles.tdBorder}>-</td>
            <td className={styles.tdBorder}>
              <span className={styles.greenDot} />
            </td>
            <td>-</td>
          </tr>
          <tr className={styles.tRow}>
            <td className={styles.titleCol}>
              {getText(NO_DEDUCTIBLE, language)}
            </td>
            {planType === PLAN_TYPE_HMO ? (
              <td className={`${styles.highlightCol} ${styles.light}`}>
                <span className={styles.greenDot} />
              </td>
            ) : (
              <td className={`${styles.darkData} ${styles.bordered}`}>
                <span className={styles.greenDot} />
              </td>
            )}
            {planType === PLAN_TYPE_PPO ? (
              <td className={`${styles.highlightCol} ${styles.light}`}>-</td>
            ) : (
              <td className={`${styles.darkData} ${styles.bordered}`}>-</td>
            )}
            {planType === PLAN_TYPE_EPO || planType === PLAN_TYPE_OAP ? (
              <td className={`${styles.highlightCol} ${styles.light}`}>-</td>
            ) : (
              <td className={`${styles.darkData} ${styles.bordered}`}>
                <span className={styles.greenDot} />
              </td>
            )}
            {planType === PLAN_TYPE_POS ? (
              <td
                className={`${styles.highlightCol} ${styles.light} ${styles.borderless}`}
              >
                <span className={styles.greenDot} />
              </td>
            ) : (
              <td className={styles.darkData}>
                <span className={styles.greenDot} />
              </td>
            )}
          </tr>
          <tr>
            <td className={styles.titleCol}>
              {getText(OUT_OF_NETWORK_COVERAGE, language)}
            </td>
            <td className={styles.tdBorder}>-</td>
            <td className={styles.tdBorder}>
              <span className={styles.greenDot} />
            </td>
            <td className={styles.tdBorder}>-</td>
            <td>
              <span className={styles.greenDot} />
            </td>
          </tr>
        </table>
        <div className={styles.oapDescription}>
          {getText(OAP_FOOTNOTE_DESCRIPTION, language as 'ENGLISH' | 'SPANISH')}
        </div>
      </div>
    </Drawer>
  );
};

export default PlanTypeInfoDrawer;
